import { Menu, X } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { UserMenu } from '@/components/layout/user-menu';
import { LandingWhatsAppWidget } from '@/components/marketing/landing-whatsapp-widget';
import { UnityLogo } from '@/components/shared/unity-logo';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';

export const LandingReveal = ({
  children,
  className,
  delayMs = 0,
  variant = 'up',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  delayMs?: number;
  variant?: 'up' | 'scale';
  as?: 'div' | 'li' | 'section' | 'ul' | 'ol' | 'p';
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const style = delayMs
    ? ({ '--landing-reveal-delay': `${delayMs}ms` } as CSSProperties)
    : undefined;

  return (
    <Tag
      ref={ref as never}
      style={style}
      className={cn(
        variant === 'scale' ? 'landing-reveal-scale' : 'landing-reveal',
        inView && 'is-inview',
        className,
      )}
    >
      {children}
    </Tag>
  );
};

const LANDING_NAV_ITEMS = [
  { to: '/', key: 'home' as const },
  { to: '/features', key: 'features' as const },
  { to: '/pricing', key: 'pricing' as const },
  { to: '/free', key: 'free' as const },
  { to: '/about', key: 'about' as const },
  { to: '/contact', key: 'contact' as const },
] as const;

export const LandingHeader = ({ signedIn }: { signedIn: boolean }) => {
  const { t } = useTranslation(['landing', 'common']);
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const navLinkClass = (active: boolean) =>
    cn(
      'landing-nav-link -mb-px border-b-2 pb-0.5 text-card-title font-semibold transition-colors',
      active ? 'border-brand text-brand' : 'border-transparent text-fg hover:text-brand',
    );

  const mobileNavLinkClass = (active: boolean) =>
    cn(
      'flex min-h-11 items-center rounded-lg px-3 text-card-title font-semibold transition-colors',
      active ? 'bg-brand/10 text-brand' : 'text-fg hover:bg-surface-sunken',
    );

  const closeMobileNav = () => setMobileNavOpen(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 border-b border-border/80 bg-background/85 backdrop-blur-md',
          mobileNavOpen && 'max-lg:invisible max-lg:pointer-events-none',
        )}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-8 lg:gap-10">
          <Link
            to="/"
            className="landing-display flex shrink-0 items-center transition-opacity hover:opacity-90"
          >
            <UnityLogo
              variant="horizontal-color"
              className="h-9 dark:[&_img]:brightness-0 dark:[&_img]:invert"
            />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label={t('nav.mainAria')}>
            {LANDING_NAV_ITEMS.map(({ to, key }) => {
              const active = pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className={navLinkClass(active)}
                  aria-current={active ? 'page' : undefined}
                >
                  {t(`nav.${key}`)}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-2 lg:hidden">
          {signedIn ? <UserMenu /> : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={mobileNavOpen ? t('common:nav.closeMenu') : t('common:nav.openMenu')}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? <X className="size-5" aria-hidden /> : <Menu className="size-5" aria-hidden />}
          </Button>
        </div>

        <div className="hidden shrink-0 items-center gap-2 sm:gap-3 lg:flex">
          {signedIn ? (
            <>
              <Button size="sm" asChild>
                <Link to="/home">{t('nav.dashboard')}</Link>
              </Button>
              <UserMenu />
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/sign-in">{t('nav.signIn')}</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/register">{t('nav.createAccount')}</Link>
              </Button>
            </>
          )}
        </div>
        </div>
      </header>

      {mobileNavOpen
        ? createPortal(
            <div className="fixed inset-0 z-[100] flex flex-col bg-background lg:hidden">
              <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4 sm:px-6">
                <Link
                  to="/"
                  className="landing-display flex shrink-0 items-center transition-opacity hover:opacity-90"
                  onClick={closeMobileNav}
                >
                  <UnityLogo
                    variant="horizontal-color"
                    className="h-9 dark:[&_img]:brightness-0 dark:[&_img]:invert"
                  />
                </Link>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('common:nav.closeMenu')}
                  onClick={closeMobileNav}
                >
                  <X className="size-5" aria-hidden />
                </Button>
              </div>

              <nav
                className="flex flex-1 flex-col gap-1 overflow-y-auto px-4 py-6 sm:px-6"
                aria-label={t('nav.mainAria')}
              >
                {LANDING_NAV_ITEMS.map(({ to, key }) => {
                  const active = pathname === to;
                  return (
                    <Link
                      key={to}
                      to={to}
                      className={mobileNavLinkClass(active)}
                      aria-current={active ? 'page' : undefined}
                      onClick={closeMobileNav}
                    >
                      {t(`nav.${key}`)}
                    </Link>
                  );
                })}
              </nav>

              <div className="shrink-0 border-t border-border bg-background px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6">
                {signedIn ? (
                  <div className="flex items-center gap-3">
                    <Button size="lg" className="flex-1" asChild>
                      <Link to="/home" onClick={closeMobileNav}>
                        {t('nav.dashboard')}
                      </Link>
                    </Button>
                    <UserMenu />
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Button size="lg" asChild>
                      <Link to="/register" onClick={closeMobileNav}>
                        {t('nav.createAccount')}
                      </Link>
                    </Button>
                    <Button variant="secondary" size="lg" asChild>
                      <Link to="/sign-in" onClick={closeMobileNav}>
                        {t('nav.signIn')}
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export const LandingFooter = ({ signedIn }: { signedIn: boolean }) => {
  const { t } = useTranslation('landing');
  const { pathname } = useLocation();
  const homeActive = pathname === '/';
  const featuresActive = pathname === '/features';
  const pricingActive = pathname === '/pricing';
  const freeActive = pathname === '/free';
  const aboutActive = pathname === '/about';
  const contactActive = pathname === '/contact';

  const footerLinkClass = (active: boolean) =>
    cn(
      'transition-colors',
      active ? 'font-medium text-brand' : 'text-fg-secondary hover:text-fg',
    );

  return (
    <footer className="border-t border-border bg-surface-sunken px-4 py-10 sm:px-6">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 sm:flex-row sm:gap-4">
        <div className="text-center sm:text-left">
          <p className="landing-display text-card-title font-semibold text-brand">{t('hero.brand')}</p>
          <p className="mt-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-brand/75">
            {t('hero.descriptor')}
          </p>
          <p className="mt-1 text-body-sm text-fg-secondary">{t('footer.tagline')}</p>
        </div>
        <div className="flex flex-col items-center gap-2 text-body-sm">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link
              to="/"
              className={footerLinkClass(homeActive)}
              aria-current={homeActive ? 'page' : undefined}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/features"
              className={footerLinkClass(featuresActive)}
              aria-current={featuresActive ? 'page' : undefined}
            >
              {t('footer.features')}
            </Link>
            <Link
              to="/pricing"
              className={footerLinkClass(pricingActive)}
              aria-current={pricingActive ? 'page' : undefined}
            >
              {t('nav.pricing')}
            </Link>
            <Link
              to="/free"
              className={footerLinkClass(freeActive)}
              aria-current={freeActive ? 'page' : undefined}
            >
              {t('nav.free')}
            </Link>
            <Link
              to="/about"
              className={footerLinkClass(aboutActive)}
              aria-current={aboutActive ? 'page' : undefined}
            >
              {t('footer.about')}
            </Link>
            <Link
              to="/contact"
              className={footerLinkClass(contactActive)}
              aria-current={contactActive ? 'page' : undefined}
            >
              {t('nav.contact')}
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/privacy" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.privacy')}
            </Link>
            <Link to="/cookies" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.cookies')}
            </Link>
            <Link to="/terms" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.terms')}
            </Link>
            <Link to="/delete-me" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.deleteAccount')}
            </Link>
            {signedIn ? (
              <Link to="/home" className="text-fg-secondary transition-colors hover:text-fg">
                {t('nav.openConsole')}
              </Link>
            ) : (
              <>
                <Link to="/sign-in" className="text-fg-secondary transition-colors hover:text-fg">
                  {t('footer.signIn')}
                </Link>
                <Link to="/register" className="text-fg-secondary transition-colors hover:text-fg">
                  {t('footer.createAccount')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export const LandingPrimaryCta = ({
  signedIn,
  size,
  className,
}: {
  signedIn: boolean;
  size: 'lg' | 'md' | 'sm';
  className?: string;
}) => {
  const { t } = useTranslation('landing');

  return (
    <Button size={size} className={className} asChild>
      <Link to={signedIn ? '/home' : '/register'}>
        {signedIn ? t('nav.openConsole') : t('hero.primaryCta')}
      </Link>
    </Button>
  );
};

export const LandingSection = ({
  id,
  title,
  subtitle,
  className,
  centered = false,
  onBrand = false,
  children,
}: {
  id?: string;
  title: string;
  subtitle: string;
  className?: string;
  centered?: boolean;
  onBrand?: boolean;
  children?: ReactNode;
}) => (
  <section
    id={id}
    className={cn('scroll-mt-20', className)}
    aria-labelledby={id ? `${id}-heading` : undefined}
  >
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <LandingReveal className={cn('max-w-2xl', centered && 'mx-auto text-center')}>
        <h2
          id={id ? `${id}-heading` : undefined}
          className={cn(
            'landing-display text-balance text-[1.75rem] font-semibold tracking-tight sm:text-[2rem]',
            onBrand ? 'text-on-brand' : 'text-fg',
          )}
        >
          {title}
        </h2>
        <p
          className={cn(
            'mt-3 text-pretty text-body leading-relaxed sm:text-card-title',
            onBrand ? 'text-on-brand/90' : 'text-fg-secondary',
          )}
        >
          {subtitle}
        </p>
      </LandingReveal>
      {children}
    </div>
  </section>
);

export const LandingMarketingShell = ({
  signedIn,
  children,
}: {
  signedIn: boolean;
  children: ReactNode;
}) => (
  <div className="landing-root min-h-dvh bg-brand text-fg">
    <LandingHeader signedIn={signedIn} />
    {children}
    <LandingFooter signedIn={signedIn} />
    <LandingWhatsAppWidget />
  </div>
);
